import { useState, useEffect, useRef } from "react";
import { authHeaders } from "../api";

// ── System logs ───────────────────────────────────────────────────────────────

const SYS_LEVELS = {
  recheck:          { color: "#00E5C0", label: "RECHECK" },
  dex_scan:         { color: "#F0B90B", label: "DEX SCAN" },
  cg_scan:          { color: "#8B8FF8", label: "CG SCAN" },
  position_monitor: { color: "#E07030", label: "POSITION" },
};

function SysBadge({ source }) {
  const l = SYS_LEVELS[source] || { color: "#555E6E", label: (source || "?").toUpperCase() };
  return (
    <span style={{
      fontSize: 8, fontWeight: 700, letterSpacing: 1, padding: "2px 7px",
      borderRadius: 4, background: l.color + "22", color: l.color,
      fontFamily: "'IBM Plex Mono',monospace", whiteSpace: "nowrap",
    }}>{l.label}</span>
  );
}

// ── Trade logs ────────────────────────────────────────────────────────────────

const TRADE_EVENTS = {
  BUY:        { color: "#00E5C0", label: "BUY" },
  SELL:       { color: "#8B8FF8", label: "SELL" },
  TP_PARTIAL: { color: "#F0B90B", label: "TP PARTIEL" },
  BUY_ERROR:  { color: "#E03030", label: "BUY ERREUR" },
  SELL_ERROR: { color: "#E03030", label: "SELL ERREUR" },
};

function TradeBadge({ event }) {
  const e = TRADE_EVENTS[event] || { color: "#555E6E", label: event || "?" };
  return (
    <span style={{
      fontSize: 8, fontWeight: 700, letterSpacing: 1, padding: "2px 7px",
      borderRadius: 4, background: e.color + "22", color: e.color,
      fontFamily: "'IBM Plex Mono',monospace", whiteSpace: "nowrap",
    }}>{e.label}</span>
  );
}

function TradeDetails({ d }) {
  if (!d || !Object.keys(d).length) return <span style={{ color: "#2A3040" }}>—</span>;
  const parts = [];
  if (d.pnl_pct !== undefined) {
    const c = d.pnl_pct >= 0 ? "#00E5C0" : "#E03030";
    parts.push(<span key="pnl" style={{ color: c, fontWeight: 700 }}>{d.pnl_pct >= 0 ? "+" : ""}{d.pnl_pct}%</span>);
  }
  if (d.amount_usdt !== undefined)
    parts.push(<span key="amt" style={{ color: "#E0E4F0" }}>${d.amount_usdt} USDT</span>);
  if (d.price !== undefined)
    parts.push(<span key="price" style={{ color: "#555E6E" }}>@${Number(d.price).toPrecision(4)}</span>);
  if (d.dex)
    parts.push(<span key="dex" style={{ color: "#2A3040" }}>{d.dex}</span>);
  if (d.reason)
    parts.push(<span key="reason" style={{ color: "#555E6E", fontStyle: "italic" }}>{d.reason}</span>);
  if (d.error)
    parts.push(<span key="err" style={{ color: "#E03030" }}>{d.error}</span>);
  if (d.tx)
    parts.push(
      <a key="tx" href={`https://bscscan.com/tx/${d.tx}`} target="_blank" rel="noreferrer"
         style={{ color: "#8B8FF8", fontSize: 9, fontFamily: "'IBM Plex Mono',monospace" }}>
        {d.tx.slice(0, 10)}…
      </a>
    );
  return <span style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 10 }}>{parts}</span>;
}

// ── Shared ────────────────────────────────────────────────────────────────────

function Timestamp({ ts }) {
  const d = ts ? new Date(ts) : null;
  if (!d) return <span style={{ color: "#2A3040" }}>—</span>;
  return (
    <div style={{ fontFamily: "'IBM Plex Mono',monospace" }}>
      <div style={{ fontSize: 9, color: "#555E6E" }}>{d.toLocaleDateString("fr-FR")}</div>
      <div style={{ fontSize: 9, color: "#2A3040" }}>{d.toLocaleTimeString("fr-FR")}</div>
    </div>
  );
}

function useAutoRefresh(fn, enabled, interval = 10_000) {
  const ref = useRef(null);
  useEffect(() => {
    if (enabled) ref.current = setInterval(fn, interval);
    else clearInterval(ref.current);
    return () => clearInterval(ref.current);
  }, [enabled]);
}

// ── Main view ─────────────────────────────────────────────────────────────────

export default function LogsView() {
  const [tab,     setTab]     = useState("trades");
  const [sysLogs, setSysLogs] = useState([]);
  const [trades,  setTrades]  = useState([]);
  const [filter,  setFilter]  = useState("ALL");
  const [search,  setSearch]  = useState("");
  const [loading, setLoading] = useState(false);
  const [auto,    setAuto]    = useState(true);

  const fetchSys = async () => {
    try {
      const r = await fetch("/api/logs", { headers: authHeaders() });
      setSysLogs(await r.json());
    } catch (_) {}
  };

  const fetchTrades = async () => {
    try {
      const r = await fetch("/api/trade-logs", { headers: authHeaders() });
      setTrades(await r.json());
    } catch (_) {}
  };

  const refresh = async () => {
    setLoading(true);
    await (tab === "trades" ? fetchTrades() : fetchSys());
    setLoading(false);
  };

  useEffect(() => { fetchTrades(); fetchSys(); }, []);
  useEffect(() => { setFilter("ALL"); setSearch(""); }, [tab]);
  useAutoRefresh(refresh, auto);

  // ── Trades tab ──
  const tradeEvents = ["ALL", ...Object.keys(TRADE_EVENTS)];
  const visibleTrades = trades.filter(t => {
    if (filter !== "ALL" && t.event !== filter) return false;
    if (search) {
      const hay = `${t.symbol} ${t.event} ${JSON.stringify(t.details || {})}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  // ── System tab ──
  const sysSources = ["ALL", ...Array.from(new Set(sysLogs.map(l => l.source).filter(Boolean)))];
  const visibleSys = sysLogs.filter(l => {
    if (filter !== "ALL" && l.source !== filter) return false;
    if (search && !`${l.message} ${l.details}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const tabStyle = active => ({
    fontSize: 10, fontWeight: 700, padding: "6px 18px", borderRadius: "6px 6px 0 0",
    border: "none", cursor: "pointer", letterSpacing: 1,
    fontFamily: "'IBM Plex Mono',monospace",
    background: active ? "#0D1117" : "transparent",
    color: active ? "#E0E4F0" : "#555E6E",
    borderBottom: active ? "2px solid #00E5C0" : "2px solid transparent",
  });

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", animation: "fadeIn .3s ease" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 4 }}>
          <button style={tabStyle(tab === "trades")} onClick={() => setTab("trades")}>
            TRADES
            {trades.length > 0 && (
              <span style={{ marginLeft: 6, fontSize: 8, background: "#00E5C022",
                             color: "#00E5C0", borderRadius: 8, padding: "1px 6px" }}>
                {trades.length}
              </span>
            )}
          </button>
          <button style={tabStyle(tab === "system")} onClick={() => setTab("system")}>
            SYSTÈME
            {sysLogs.length > 0 && (
              <span style={{ marginLeft: 6, fontSize: 8, background: "#E0303022",
                             color: "#E03030", borderRadius: 8, padding: "1px 6px" }}>
                {sysLogs.length}
              </span>
            )}
          </button>
        </div>
        {loading && <span style={{ fontSize: 9, color: "#555E6E" }}>↻</span>}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={() => setAuto(v => !v)} style={{
            fontSize: 9, padding: "4px 10px", borderRadius: 5, border: "none", cursor: "pointer",
            background: auto ? "#00E5C020" : "#1C2030", color: auto ? "#00E5C0" : "#555E6E",
            fontFamily: "'IBM Plex Mono',monospace",
          }}>{auto ? "⟳ AUTO" : "⟳ PAUSE"}</button>
          <button onClick={refresh} style={{
            fontSize: 9, padding: "4px 10px", borderRadius: 5,
            border: "1px solid #1C2030", background: "transparent", color: "#555E6E", cursor: "pointer",
          }}>Rafraîchir</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {(tab === "trades" ? tradeEvents : sysSources).map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{
            fontSize: 9, padding: "4px 10px", borderRadius: 5, cursor: "pointer",
            border: `1px solid ${filter === s ? "#00E5C044" : "#1C2030"}`,
            background: filter === s ? "#00E5C022" : "transparent",
            color: filter === s ? "#00E5C0" : "#555E6E",
            fontFamily: "'IBM Plex Mono',monospace", letterSpacing: 1,
          }}>
            {s === "ALL" ? "TOUS" : (tab === "trades"
              ? (TRADE_EVENTS[s]?.label || s)
              : (SYS_LEVELS[s]?.label || s.toUpperCase()))}
          </button>
        ))}
        <input
          placeholder="Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            marginLeft: "auto", fontSize: 10, padding: "4px 10px", borderRadius: 5,
            border: "1px solid #1C2030", background: "#0D1117", color: "#E0E4F0",
            outline: "none", width: 180,
          }}
        />
      </div>

      {/* Table */}
      <div style={{ background: "#0D1117", border: "1px solid #1C2030", borderRadius: 10, overflow: "hidden" }}>

        {/* ── TRADES ── */}
        {tab === "trades" && (
          visibleTrades.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#2A3040", fontSize: 12 }}>
              {trades.length === 0 ? "Aucun trade enregistré" : "Aucun résultat"}
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #1C2030" }}>
                  {["Date", "Événement", "Token", "Détails"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left",
                      fontSize: 8, color: "#2A3040", letterSpacing: 2, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleTrades.map((t, i) => (
                  <tr key={t._id || i} style={{
                    borderBottom: "1px solid #0D0F14",
                    background: i % 2 === 0 ? "transparent" : "#0A0C1044",
                  }}>
                    <td style={{ padding: "10px 14px" }}><Timestamp ts={t.ts} /></td>
                    <td style={{ padding: "10px 14px" }}><TradeBadge event={t.event} /></td>
                    <td style={{ padding: "10px 14px", fontSize: 11, color: "#E0E4F0",
                                 fontWeight: 700, fontFamily: "'IBM Plex Mono',monospace" }}>
                      {t.symbol || "—"}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <TradeDetails d={t.details} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {/* ── SYSTEM ── */}
        {tab === "system" && (
          visibleSys.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#2A3040", fontSize: 12 }}>
              {sysLogs.length === 0 ? "Aucune erreur enregistrée" : "Aucun résultat"}
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #1C2030" }}>
                  {["Date", "Source", "Détails", "Message"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left",
                      fontSize: 8, color: "#2A3040", letterSpacing: 2, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleSys.map((log, i) => (
                  <tr key={log._id || i} style={{
                    borderBottom: "1px solid #0D0F14",
                    background: i % 2 === 0 ? "transparent" : "#0A0C1044",
                  }}>
                    <td style={{ padding: "10px 14px" }}><Timestamp ts={log.ts} /></td>
                    <td style={{ padding: "10px 14px" }}><SysBadge source={log.source} /></td>
                    <td style={{ padding: "10px 14px", fontSize: 10, color: "#8B8FF8",
                                 fontFamily: "'IBM Plex Mono',monospace", maxWidth: 120,
                                 overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {log.details || "—"}
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 10, color: "#E03030",
                                 maxWidth: 400, wordBreak: "break-word", lineHeight: 1.5 }}>
                      {log.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      <div style={{ marginTop: 8, fontSize: 9, color: "#2A3040", textAlign: "right" }}>
        {tab === "trades" ? visibleTrades.length : visibleSys.length} entrée{((tab === "trades" ? visibleTrades : visibleSys).length !== 1) ? "s" : ""}
      </div>
    </div>
  );
}
